import type { ImgHTMLAttributes } from 'react'
import {
  cmsImageSrcSet,
  cmsImageUrl,
  CMS_IMAGE_WIDTHS,
  CMS_STYLE_WIDTH,
  type CmsImageStyle,
  type CmsImageWidth,
} from '../lib/cms-image'
import { handleCmsImageError } from '../lib/img-fallback'

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  src: string | null | undefined
  size?: CmsImageStyle
}

/** Next width up, for the 2x variant (clamped to the largest we generate). */
function retinaWidth(width: CmsImageWidth): CmsImageWidth {
  const doubled = width * 2
  return (
    CMS_IMAGE_WIDTHS.find((w) => w >= doubled) ?? CMS_IMAGE_WIDTHS[CMS_IMAGE_WIDTHS.length - 1]
  )
}

export function CmsImage({ src, size = 'md', alt = '', onError, sizes, ...rest }: Props) {
  const url = cmsImageUrl(src, size)
  if (!url) return null

  const base = CMS_STYLE_WIDTH[size]
  const retina = retinaWidth(base)
  // With an explicit `sizes` the browser can pick by layout width; without one
  // it would assume 100vw, so fall back to plain pixel-density variants.
  const widths: CmsImageWidth[] = sizes
    ? [...new Set(CMS_IMAGE_WIDTHS.filter((w) => w <= retina && w >= Math.min(320, base)))]
    : []
  const srcSet = sizes
    ? cmsImageSrcSet(src, widths)
    : densitySrcSet(src, base, retina)

  return (
    <img
      src={url}
      srcSet={srcSet}
      sizes={srcSet && sizes ? sizes : undefined}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={(e) => {
        handleCmsImageError(e)
        onError?.(e)
      }}
      {...rest}
    />
  )
}

function densitySrcSet(
  src: string | null | undefined,
  base: CmsImageWidth,
  retina: CmsImageWidth,
): string | undefined {
  if (retina === base) return undefined
  const oneX = cmsImageSrcSet(src, [base])
  const twoX = cmsImageSrcSet(src, [retina])
  if (!oneX || !twoX) return undefined
  return `${oneX.replace(/ \d+w$/, ' 1x')}, ${twoX.replace(/ \d+w$/, ' 2x')}`
}
